import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  ArrowRight, 
  BarChart3, 
  CheckCircle2, 
  Target, 
  Briefcase, 
  Video, 
  Zap, 
  ShieldCheck,
  AlertCircle,
  Loader2,
  XCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { apiClient } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

interface ArtieScoreStatusResponse {
  status: "processing" | "completed" | "failed";
  task_id?: string;
  score_data?: ArtieScoreData;
  error?: string;
  progress?: string;
}

interface ArtieScoreData {
  artie_score: number;
  score_label: string;
  score_tone: string;
  budget_viability: string;
  budget_viability_explanation: string;
  reach_status: string;
  brand_fit_status: string;
  production_readiness_status: string;
  reach_analysis: {
    status: string;
    current_subscribers?: number;
    avg_views?: number;
    engagement_rate?: number;
    expected_reach_range?: { low: number; mid: number; high: number };
    sufficient_for_target: boolean;
    gap_analysis?: string;
  };
  production_readiness_analysis: {
    status: string;
    budget_stated: boolean;
    budget_amount?: number;
    execution_signals: string[];
    gaps: string[];
  };
  brand_fit_analysis: {
    status: string;
    budget_viability: string;
    brand_safety: {
      risk_level: string;
      safe_for_brands: boolean;
      concerns: string[];
    };
    category_mapping: {
      primary_categories: string[];
      genre?: string;
      themes: string[];
    };
    premium_index: {
      tier: string;
      genre_desirability: string;
    };
    integration_opportunities: {
      potential_level: string;
      visible_logo_opportunities: number;
      product_mention_opportunities: number;
      product_use_opportunities: number;
    };
    accessible_brand_pool?: number;
    craft_assessment: {
      overall_craft_level: string;
      specific_strengths: string[];
      specific_weaknesses: string[];
    };
  };
  recommendations: Array<{
    priority: number;
    what_to_improve: string;
    why_it_matters: string;
    how_to_improve: string;
    related_metric: string;
  }>;
}

const ArtieScorePage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scoreData, setScoreData] = useState<ArtieScoreData | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      if (!projectId) return;
      try {
        const project = await apiClient.getProject(projectId);
        
        // Production Logic:
        // If project is NOT reviewed (default), we redirect to pending (blocking access).
        // If project IS reviewed (true), we allow access.
        if (!project.is_reviewed) {
          navigate(`/projects/${projectId}/pending`);
        }
      } catch (error) {
        console.error("Failed to check project status", error);
      }
    };
    
    checkAccess();

    const pollStatus = async () => {
      if (!projectId) return;

      try {
        const statusResponse = await apiClient.getArtieScoreStatus(projectId) as ArtieScoreStatusResponse;
        console.log("Status Response:", statusResponse);

        // CRITICAL: Only consider it complete if we have BOTH completed status AND score data
        // This prevents stopping after script processing completes but before score calculation
        if (statusResponse.status === "completed" && statusResponse.score_data) {
          // Calculation completed successfully WITH score data
          console.log("✓ Artie Score calculation completed successfully with score data!");
          setScoreData(statusResponse.score_data as ArtieScoreData);
          setLoading(false);
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
        } else if (statusResponse.status === "completed" && !statusResponse.score_data) {
          // Status is completed but no score data yet - this means script processing finished
          // but Artie Score calculation hasn't completed yet. Keep polling!
          console.log("Script processing complete, waiting for Artie Score calculation...");
        } else if (statusResponse.status === "failed") {
          // Calculation failed
          console.error("✗ Calculation failed:", statusResponse.error);
          setError(statusResponse.error || "Failed to calculate Artie Score");
          setLoading(false);
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          toast({
            title: "Error",
            description: statusResponse.error || "Failed to calculate Artie Score",
            variant: "destructive",
          });
        } else {
          // If status is "processing", keep loading true and continue polling
          console.log("Still processing, continuing to poll...");
        }
      } catch (err: any) {
        // For ANY error during polling, just log and continue
        // Don't stop polling unless it's a critical error
        console.warn("Error during status poll (will retry):", err.message || err);
        // Continue polling - don't stop on temporary errors
      }
    };

    const fetchArtieScore = async () => {
      if (!projectId) {
        setError("No project ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // First, check if there's already a calculation in progress
        const statusCheck = await apiClient.getArtieScoreStatus(projectId) as ArtieScoreStatusResponse;
        console.log("Initial Status Check:", statusCheck);
        
        if (statusCheck.status === "processing") {
          // Already processing, just start polling
          console.log("Calculation already in progress, starting polling");
          const interval = setInterval(pollStatus, 3000);
          setPollingInterval(interval);
          return;
        } else if (statusCheck.status === "completed" && statusCheck.score_data) {
          // Already completed, show results
          console.log("Calculation already completed, showing results");
          setScoreData(statusCheck.score_data as ArtieScoreData);
          setLoading(false);
          return;
        }
        
        // No existing calculation, trigger a new one
        console.log("No existing calculation, triggering new one");
        const response = await apiClient.calculateArtieScore(projectId) as ArtieScoreStatusResponse;
        console.log("Artie Score Response:", response);
        
        // Check if we got immediate results (sync fallback) or async processing
        if (response.status === "processing") {
          // Start polling for status - keep loading true during polling
          console.log("Starting polling, loading will stay true until completion");
          const interval = setInterval(pollStatus, 3000); // Poll every 3 seconds
          setPollingInterval(interval);
          
          // Set a safety timeout to prevent infinite polling (5 minutes)
          setTimeout(() => {
            if (pollingInterval) {
              clearInterval(pollingInterval);
              setPollingInterval(null);
              setLoading(false);
              setError("Calculation is taking longer than expected. Please refresh the page.");
              toast({
                title: "Timeout",
                description: "Calculation is taking longer than expected. Please refresh the page.",
                variant: "destructive",
              });
            }
          }, 300000); // 5 minutes
          
          // loading stays true - will be set false by pollStatus when complete
        } else if (response.status === "completed" && response.score_data) {
          // Immediate completion (sync fallback)
          setScoreData(response.score_data as ArtieScoreData);
          setLoading(false);
        } else {
          // Unexpected response
          setError("Unexpected response from server");
          setLoading(false);
        }
      } catch (err: any) {
        console.error("Error fetching Artie Score:", err);
        setError(err.message || "Failed to calculate Artie Score");
        setLoading(false);
        toast({
          title: "Error",
          description: err.message || "Failed to calculate Artie Score",
          variant: "destructive",
        });
      }
    };

    fetchArtieScore();

    // Cleanup polling on unmount
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]); // Only re-run when projectId changes

  const handleNext = () => {
    navigate(projectId ? `/projects/${projectId}` : "/dashboard");
  };

  // Helper function to get traffic light color
  const getTrafficLightColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "green":
        return "text-green-600";
      case "yellow":
        return "text-yellow-600";
      case "red":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  // Helper function to get score color
  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  // Helper function to format score label
  const formatScoreLabel = (label: string) => {
    return label.split("_").map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(" ");
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto" />
          <h2 className="text-2xl font-bold text-slate-900">Calculating Your Artie Score</h2>
          <p className="text-slate-600">
            Analyzing your project with AI models. This may take 2-3 minutes as we:
          </p>
          <ul className="text-sm text-slate-500 text-left space-y-2 mt-4">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
              <span>Sync your YouTube channel data</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
              <span>Process your script content</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
              <span>Calculate reach, brand fit, and production readiness</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
              <span>Generate personalized recommendations</span>
            </li>
          </ul>
          <p className="text-xs text-slate-400 mt-4">
            Please keep this page open while we process your score...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !scoreData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <XCircle className="h-12 w-12 text-red-500 mx-auto" />
              <h2 className="text-2xl font-bold text-slate-900">Error</h2>
              <p className="text-slate-600">{error || "Failed to load Artie Score"}</p>
              <Button onClick={() => navigate(`/projects/${projectId}`)}>
                Back to Project
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Helper function to parse and format recommendations
  const formatRecommendation = (text: string) => {
    const listItems = text.split(/\d+\.\s+\*\*/).filter(item => item.trim());
    
    if (listItems.length > 1) {
      return listItems.map((item, idx) => {
        const parts = item.split(':**');
        const title = parts[0]?.trim();
        const description = parts[1]?.trim();
        return { title, description };
      });
    }
    return null;
  };

  // Map dimensions for display
  const dimensions = [
    {
      id: "reach",
      title: "Reach Potential",
      score: scoreData.reach_analysis.sufficient_for_target ? 85 : 65,
      status: scoreData.reach_status,
      statusColor: getTrafficLightColor(scoreData.reach_status),
      icon: <Target className="h-6 w-6 text-blue-500" />,
      definition: "Estimated audience size and engagement based on your YouTube channel.",
      output: scoreData.reach_analysis.current_subscribers
        ? `${scoreData.reach_analysis.current_subscribers.toLocaleString()} subscribers, ${scoreData.reach_analysis.avg_views?.toLocaleString() || 'N/A'} avg views`
        : "YouTube data being analyzed",
      recommendation: scoreData.recommendations.find(r => r.related_metric === "reach")?.how_to_improve ||
        scoreData.reach_analysis.gap_analysis ||
        "Continue building your audience and engagement.",
    },
    {
      id: "brand_fit",
      title: "Brand Fit",
      score: scoreData.brand_fit_analysis.budget_viability === "achievable" ? 85 :
             scoreData.brand_fit_analysis.budget_viability === "stretch" ? 70 : 55,
      status: scoreData.brand_fit_status,
      statusColor: getTrafficLightColor(scoreData.brand_fit_status),
      icon: <Briefcase className="h-6 w-6 text-purple-500" />,
      definition: "Suitability for brand integration and advertiser interest.",
      output: `${scoreData.brand_fit_analysis.category_mapping.primary_categories.slice(0, 3).join(", ")} | ${scoreData.brand_fit_analysis.accessible_brand_pool || 'Many'} potential brands`,
      recommendation: scoreData.recommendations.find(r => r.related_metric === "brand_fit")?.how_to_improve ||
        "Optimize content for brand safety and integration opportunities.",
    },
    {
      id: "readiness",
      title: "Production Readiness",
      score: scoreData.brand_fit_analysis.craft_assessment.overall_craft_level === "strong" ? 85 :
             scoreData.brand_fit_analysis.craft_assessment.overall_craft_level === "adequate" ? 70 : 55,
      status: scoreData.production_readiness_status,
      statusColor: getTrafficLightColor(scoreData.production_readiness_status),
      icon: <Video className="h-6 w-6 text-green-500" />,
      definition: "Script quality and production preparedness.",
      output: `Craft: ${formatScoreLabel(scoreData.brand_fit_analysis.craft_assessment.overall_craft_level)} | Budget: ${scoreData.production_readiness_analysis.budget_stated ? 'Stated' : 'Pending'}`,
      recommendation: scoreData.recommendations.find(r => r.related_metric === "production_readiness")?.how_to_improve ||
        "Continue refining your script and production plan.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-8">
        
        {/* Header Section */}
        <div className="text-center space-y-4 mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-indigo-100 rounded-full mb-4">
            <Zap className="h-8 w-8 text-indigo-600" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
            Your Artie Score
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            {scoreData.score_tone}
          </p>
          
          <div className="flex justify-center items-center space-x-2 mt-4">
            <Badge variant="outline" className="bg-white border-slate-200 text-slate-600 px-3 py-1">
              <ShieldCheck className="w-3 h-3 mr-1 text-green-500" />
              {formatScoreLabel(scoreData.score_label)}
            </Badge>
            <Badge variant="outline" className="bg-white border-slate-200 text-slate-600 px-3 py-1">
              Budget: {formatScoreLabel(scoreData.budget_viability)}
            </Badge>
          </div>
        </div>

        {/* Overall Score Card */}
        <Card className="border-none shadow-lg bg-white overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500" />
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left space-y-2">
                <h2 className="text-2xl font-semibold text-slate-900">Overall Potential</h2>
                <p className="text-slate-500 max-w-md">
                  {scoreData.budget_viability_explanation}
                </p>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="relative flex items-center justify-center w-32 h-32 rounded-full bg-slate-50 border-4 border-slate-100">
                  <div className="text-center">
                    <span className={`text-4xl font-bold ${getScoreColor(scoreData.artie_score)}`}>
                      {scoreData.artie_score}
                    </span>
                    <span className="block text-xs text-slate-400 font-medium uppercase tracking-wide">/ 100</span>
                  </div>
                  <svg className="absolute w-full h-full -rotate-90 pointer-events-none">
                    <circle
                      cx="60"
                      cy="60"
                      r="54"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="8"
                      strokeDasharray="339.292"
                      strokeDashoffset={339.292 - (339.292 * scoreData.artie_score) / 100}
                      className="text-indigo-600"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dimensions Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {dimensions.map((dim) => (
            <Card key={dim.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    {dim.icon}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-lg font-bold ${getScoreColor(dim.score)}`}>
                      {dim.score}
                    </span>
                    <Badge
                      variant="outline"
                      className={`text-xs font-semibold ${dim.statusColor}`}
                    >
                      {dim.status.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                <CardTitle className="text-xl text-slate-900">{dim.title}</CardTitle>
                <CardDescription className="text-slate-500 text-sm line-clamp-2 min-h-[40px]">
                  {dim.definition}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-xs mb-1 font-medium text-slate-500">
                    <span>Performance</span>
                    <span>{dim.status}</span>
                  </div>
                  <Progress value={dim.score} className="h-2" />
                </div>
                
                <div className="bg-slate-50 p-3 rounded-md border border-slate-100 space-y-3">
                  <div>
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider block mb-1">
                      Current Status
                    </span>
                    <p className="text-sm text-slate-800">{dim.output}</p>
                  </div>
                  <Separator className="bg-slate-200" />
                  <div>
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider block mb-1">
                      Recommendation
                    </span>
                    {(() => {
                      const formattedRec = formatRecommendation(dim.recommendation);
                      
                      if (formattedRec) {
                        return (
                          <ol className="list-decimal list-inside space-y-1.5 text-sm">
                            {formattedRec.map((item, idx) => (
                              <li key={idx} className="text-slate-700">
                                <span className="font-semibold">{item.title}:</span>
                                {item.description && <span className="font-normal"> {item.description}</span>}
                              </li>
                            ))}
                          </ol>
                        );
                      }
                      
                      return (
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                          <p className="text-sm text-slate-600">{dim.recommendation}</p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Top Recommendations */}
        {scoreData.recommendations.length > 0 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-slate-900 flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-indigo-600" />
                Top Recommendations
              </CardTitle>
              <CardDescription>
                Prioritized actions to improve your score and reach your goal
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {scoreData.recommendations.slice(0, 3).map((rec, idx) => {
                // Parse numbered list items from how_to_improve text
                const howToImproveText = rec.how_to_improve || '';
                const listItems = howToImproveText.split(/\d+\.\s+\*\*/).filter(item => item.trim());
                
                return (
                  <div key={idx} className="border-l-4 border-indigo-600 pl-4 py-2">
                    <div className="flex items-start gap-3">
                      <Badge variant="outline" className="mt-1">
                        Priority {rec.priority}
                      </Badge>
                      <div className="flex-1 space-y-2">
                        <h4 className="font-semibold text-slate-900">{rec.what_to_improve}</h4>
                        <p className="text-sm text-slate-600">{rec.why_it_matters}</p>
                        
                        {/* Display as formatted list if numbered items detected */}
                        {listItems.length > 1 ? (
                          <ol className="list-decimal list-inside space-y-2 text-sm text-indigo-600 font-medium">
                            {listItems.map((item, itemIdx) => {
                              // Extract title and description
                              const parts = item.split(':**');
                              const title = parts[0]?.trim();
                              const description = parts[1]?.trim();
                              
                              return (
                                <li key={itemIdx} className="pl-2">
                                  <span className="font-semibold">{title}:</span>
                                  {description && <span className="font-normal text-slate-700"> {description}</span>}
                                </li>
                              );
                            })}
                          </ol>
                        ) : (
                          <p className="text-sm text-indigo-600 font-medium">{howToImproveText}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Footer / Next Steps */}
        <Card className="bg-slate-900 text-white border-none">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="space-y-2">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-400" />
                  What's Next?
                </h3>
                <p className="text-slate-300 max-w-lg">
                  This score is based on your current project details and YouTube data.
                  As you refine your project, Artie will update these metrics and provide new insights.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate(projectId ? `/projects/${projectId}/pitch-deck` : "/dashboard")}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-lg px-8 py-6 rounded-full font-semibold transition-all"
                >
                  Generate Pitch Deck
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  onClick={handleNext}
                  className="bg-white text-slate-900 hover:bg-slate-100 text-lg px-8 py-6 rounded-full font-semibold transition-all shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]"
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default ArtieScorePage;